import Pikaday from "pikaday";

export default class JPDatepicker
{
	static selector()
	{
		return ".jp-datepicker";
	}

	static classInitialized()
	{
		return "jp-datepicker--ready";
	}

	static initialize()
	{
		document.querySelectorAll(this.selector()).forEach((el) => {
			this.initializeElement(el);
		});

		document.addEventListener("jp-dialog-opened", (event) => {
			const dialog = event.target;
			if (!(dialog instanceof HTMLElement)) {
				return;
			}
			dialog.querySelectorAll(this.selector()).forEach((el) => {
				this.initializeElement(el);
			});
		});
	}

	/**
	 * Center a bound Pikaday panel under the diary header date field only.
	 * Other datepickers keep Pikaday’s default bottom-left alignment.
	 * @param {Pikaday} picker
	 * @returns {boolean}
	 */
	static centerUnderField(picker)
	{
		if (!picker || !picker._o || picker._o.container) {
			return false;
		}

		const field = picker._o.trigger || picker._o.field;
		const el = picker.el;
		if (!field || !el) {
			return false;
		}

		// Only the diary day browser in the page header.
		if (!field.closest(".jp-diary__date-browser")) {
			return false;
		}

		const fieldRect = field.getBoundingClientRect();
		const width = el.offsetWidth;
		if (!width) {
			return false;
		}

		const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
		const pageX = window.pageXOffset || 0;
		let left = fieldRect.left + pageX + ((fieldRect.width - width) / 2);
		const minLeft = pageX + 8;
		const maxLeft = pageX + viewportWidth - width - 8;
		if (maxLeft >= minLeft) {
			left = Math.min(Math.max(left, minLeft), maxLeft);
		}

		el.style.left = `${left}px`;
		el.classList.remove("left-aligned", "right-aligned");
		el.classList.add("center-aligned");

		return true;
	}

	/**
	 * Position in the same turn as Pikaday’s adjustPosition so the default
	 * left alignment never paints. CSS hides the panel until .center-aligned.
	 * @param {Pikaday} picker
	 */
	static bindCenteredHeaderPosition(picker)
	{
		picker.el.classList.add("jp-datepicker-widget--header-center");

		const originalHide = picker.hide.bind(picker);
		const originalAdjustPosition = picker.adjustPosition.bind(picker);

		picker.hide = function hideCentered() {
			originalHide();
			this.el.classList.remove("center-aligned");
		};

		picker.adjustPosition = function adjustPositionCentered() {
			originalAdjustPosition();
			if (JPDatepicker.centerUnderField(this)) {
				return;
			}

			requestAnimationFrame(() => {
				JPDatepicker.centerUnderField(this);
			});
		};
	}

	/**
	 * Pikaday skips adjustPosition when `container` is set (needed so the
	 * panel stays in the dialog top layer). Without this, a flex dialog
	 * centers the unbound calendar in the middle of the modal.
	 * @param {Pikaday} picker
	 * @returns {boolean}
	 */
	static alignToFieldInContainer(picker)
	{
		if (!picker || !picker._o || !picker._o.container) {
			return false;
		}

		const field = picker._o.trigger || picker._o.field;
		const el = picker.el;
		const container = picker._o.container;
		if (!field || !el || !container) {
			return false;
		}

		const fieldRect = field.getBoundingClientRect();
		const containerRect = container.getBoundingClientRect();
		const width = el.offsetWidth || 320;
		const height = el.offsetHeight || 320;
		const gap = 4;

		let left = fieldRect.left - containerRect.left + (container.scrollLeft || 0);
		let top = fieldRect.bottom - containerRect.top + (container.scrollTop || 0) + gap;

		const maxLeft = Math.max(8, containerRect.width - width - 8);
		left = Math.min(Math.max(left, 8), maxLeft);

		if (top + height > containerRect.height - 8 && fieldRect.top - containerRect.top - height - gap > 8) {
			top = fieldRect.top - containerRect.top + (container.scrollTop || 0) - height - gap;
			el.classList.add("top-aligned");
			el.classList.remove("bottom-aligned");
		} else {
			el.classList.add("bottom-aligned");
			el.classList.remove("top-aligned");
		}

		el.style.position = "absolute";
		el.style.left = `${left}px`;
		el.style.top = `${top}px`;
		el.classList.add("left-aligned");
		el.classList.remove("right-aligned");

		return true;
	}

	/**
	 * @param {Pikaday} picker
	 */
	static bindDialogFieldPosition(picker)
	{
		picker.el.classList.add("jp-datepicker-widget--dialog");

		const originalAdjustPosition = picker.adjustPosition.bind(picker);

		picker.adjustPosition = function adjustPositionInDialog() {
			originalAdjustPosition();
			if (JPDatepicker.alignToFieldInContainer(this)) {
				return;
			}

			requestAnimationFrame(() => {
				JPDatepicker.alignToFieldInContainer(this);
			});
		};
	}

	/**
	 * Diary header: picking a day should navigate, same as the Přejít submit.
	 * Other jp-datepicker fields (dialogs, fasting) only fill the input.
	 * @param {HTMLElement} el
	 */
	static submitDiaryDateForm(el)
	{
		const form = el && el.closest(".jp-diary__date-form");
		if (!form) {
			return;
		}

		if (typeof form.requestSubmit === "function") {
			form.requestSubmit();
			return;
		}

		form.submit();
	}

	static initializeElement(el)
	{
		if (!el || el.classList.contains(this.classInitialized())) {
			return;
		}

		if (!el.placeholder || !String(el.placeholder).trim()) {
			el.placeholder = "d.m.r";
		}

		const dialog = el.closest("dialog");
		const centerInHeader = Boolean(el.closest(".jp-diary__date-browser"));
		const options = {
			field: el,
			firstDay: 1,
			format: "D.M.YYYY",
			keyboardInput: false,
			theme: "jp-datepicker-widget",
			yearRange: 100,
			i18n: {
				previousMonth: "Předchozí měsíc",
				nextMonth: "Následující měsíc",
				months: ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"],
				weekdays: ["Neděle", "Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota"],
				weekdaysShort: ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"],
			},
		};

		if (centerInHeader) {
			options.onSelect = () => {
				JPDatepicker.submitDiaryDateForm(el);
			};
		}

		if (dialog) {
			options.container = dialog;
		}

		const picker = new Pikaday(options);
		if (centerInHeader) {
			JPDatepicker.bindCenteredHeaderPosition(picker);
		} else if (dialog) {
			JPDatepicker.bindDialogFieldPosition(picker);
		}
		el.classList.add(this.classInitialized());
	}
}
